import React, { useState } from "react";
import { useGetWebinars, useDeleteWebinar } from "./hooks";
import { Webinar, getThumbnailUrl } from "./api";
import NewWebinarModal from "./NewWebinarModal";
import { useI18n, useT } from "../../i18n";

const formatDate = (value: string | undefined, locale: string) => {
  if (!value) return "";
  return new Date(value).toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const sourceLabelKeys: Record<string, string> = {
  youtube: "webinars.sources.youtube",
  vimeo: "webinars.sources.vimeo",
  zoom: "webinars.sources.zoom",
  iframe: "webinars.sourceLink",
};

const sourceIcon: Record<string, string> = {
  youtube: "fa-brands fa-youtube",
  vimeo: "fa-brands fa-vimeo-v",
  zoom: "fa-solid fa-video",
  iframe: "fa-solid fa-link",
};

const WebinarCard: React.FC<{
  item: Webinar;
  onEdit: (w: Webinar) => void;
  onDelete: (w: Webinar) => void;
}> = ({ item, onEdit, onDelete }) => {
  const { t, code } = useI18n();
  const thumb = getThumbnailUrl(item.videoUrl, item.source);

  return (
    <div className="group relative bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      {/* Thumbnail / preview */}
      <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={item.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <i
            className={`${
              sourceIcon[item.source] || "fa-solid fa-video"
            } text-4xl text-purple-300`}
          ></i>
        )}

        {/* Play overlay */}
        <a
          href={item.videoUrl}
          target="_blank"
          rel="noreferrer"
          className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors"
          title={t("webinars.openVideo")}
        >
          <span className="opacity-0 group-hover:opacity-100 transition-opacity h-12 w-12 rounded-full bg-white/90 flex items-center justify-center shadow">
            <i className="fa-solid fa-play text-gray-800 ml-0.5"></i>
          </span>
        </a>

        {/* Source badge */}
        <span className="absolute top-3 right-3 text-xs font-medium px-2 py-1 rounded-md bg-black/60 text-white flex items-center gap-1">
          <i className={`${sourceIcon[item.source]} text-[10px]`}></i>
          {sourceLabelKeys[item.source]
            ? t(sourceLabelKeys[item.source])
            : item.source}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-base font-semibold text-gray-900 line-clamp-1">
          {item.title}
        </h3>

        <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <i className="fa-regular fa-calendar"></i>
            {formatDate(item.createdAt, code)}
          </span>
        </div>

        {item.description && (
          <p className="mt-2 text-sm text-gray-500 line-clamp-2">
            {item.description}
          </p>
        )}

        {/* Actions */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(item)}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <i className="fa-solid fa-pen mr-1.5"></i> {t("common.edit")}
          </button>
          <button
            onClick={() => onDelete(item)}
            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <i className="fa-solid fa-trash mr-1.5"></i> {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-1/2" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-8 bg-gray-100 rounded w-full mt-2" />
    </div>
  </div>
);

export default function WebinarPage() {
  const t = useT();
  const [modalOpen, setModalOpen] = useState(false);
  const [editWebinar, setEditWebinar] = useState<Webinar | null>(null);

  const { data: webinars = [], isLoading, error } = useGetWebinars();
  const deleteWebinar = useDeleteWebinar();

  const handleAdd = () => {
    setEditWebinar(null);
    setModalOpen(true);
  };
  const handleEdit = (webinar: Webinar) => {
    setEditWebinar(webinar);
    setModalOpen(true);
  };
  const handleDelete = (webinar: Webinar) => {
    if (window.confirm(t("webinars.confirmDelete", { title: webinar.title }))) {
      deleteWebinar.mutate(webinar.id);
    }
  };

  const list: Webinar[] = webinars;

  return (
    <div className="flex flex-col flex-1 h-full bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white px-4 sm:px-8 pt-5 pb-5 border-b border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sticky top-0 z-20">
        <div className="flex flex-col gap-1">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t("webinars.title")}</h1>
          <p className="text-xs sm:text-sm text-gray-500 max-w-2xl">
            {t("webinars.subtitle")}
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2.5 flex items-center justify-center bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold shadow-sm w-full sm:w-auto"
        >
          <i className="fa-solid fa-plus mr-2"></i> {t("webinars.addWebinar")}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 px-4 sm:px-8 py-6">
        {/* Loading */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center">
            <i className="fa-solid fa-triangle-exclamation text-3xl text-red-400"></i>
            <p className="mt-3 text-sm text-red-600">
              {t("webinars.loadFailed")}
            </p>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && list.length === 0 && (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-50 flex items-center justify-center">
              <i className="fa-solid fa-video text-2xl text-red-500"></i>
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-800">
              {t("webinars.empty.title")}
            </h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              {t("webinars.empty.body")}
            </p>
            <button
              onClick={handleAdd}
              className="mt-5 px-4 py-2.5 inline-flex items-center bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-semibold"
            >
              <i className="fa-solid fa-plus mr-2"></i> {t("webinars.addWebinar")}
            </button>
          </div>
        )}

        {/* Grid */}
        {!isLoading && !error && list.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {list.map((item) => (
              <WebinarCard
                key={item.id}
                item={item}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <NewWebinarModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        editWebinar={editWebinar}
      />
    </div>
  );
}
